import type { LandingTemplateButtonsMap, LandingTemplateEditDraft } from './landingTemplateButtons'
import type {
  LANDING_BUTTON_MODE_MODULE_OBJECT,
} from '~/shared/const/landingTemplateRoute'
import {
  hasLandingTemplateFlowQuery,
  LANDING_BUTTON_MODE_MODULE,
  omitLandingTemplateFlowQuery,
} from '~/shared/const/landingTemplateRoute'
import { normalizeLandingTemplateButtons, serializeLandingTemplateButtonsForSave } from './landingTemplateButtons'
import { useLandingTemplateEditDraft } from './useLandingTemplateEditDraft'

export function useTemplateEditButtons(options: {
  templateNumericId: string
  editRoutePermission: string
  getDraftPayload: () => LandingTemplateEditDraft
  applyDraftPayload: (draft: LandingTemplateEditDraft) => void
}) {
  const buttons = ref<LandingTemplateButtonsMap>({})
  const isButtonTypePopupOpen = ref(false)
  const pendingButtonSlot = ref('')

  const route = useRoute()
  const router = useRouter()

  const {
    navigateToModuleSelect,
    clearDraft,
    readDraft,
  } = useLandingTemplateEditDraft(options.templateNumericId)

  onMounted(() => {
    const draft = readDraft()
    if (draft) {
      options.applyDraftPayload(draft)
    }

    if (hasLandingTemplateFlowQuery(route.query)) {
      void router.replace({
        name: route.name ?? undefined,
        params: route.params,
        query: omitLandingTemplateFlowQuery(route.query),
      })
    }
  })

  function loadButtonsFromContent(contentButtons?: LandingTemplateButtonsMap | null) {
    buttons.value = normalizeLandingTemplateButtons(contentButtons)
  }

  function handleStartAddButton(slot: string) {
    pendingButtonSlot.value = slot
    isButtonTypePopupOpen.value = true
  }

  function handleOpenModuleSelect(slot: string) {
    navigateToModuleSelect(
      slot,
      options.editRoutePermission,
      options.getDraftPayload(),
      LANDING_BUTTON_MODE_MODULE,
    )
  }

  function handleChooseButtonType(mode: typeof LANDING_BUTTON_MODE_MODULE | typeof LANDING_BUTTON_MODE_MODULE_OBJECT) {
    if (!pendingButtonSlot.value) {
      return
    }

    navigateToModuleSelect(
      pendingButtonSlot.value,
      options.editRoutePermission,
      options.getDraftPayload(),
      mode,
    )
    pendingButtonSlot.value = ''
  }

  function handleClearButton(slot: string) {
    buttons.value = {
      ...buttons.value,
      [slot]: null,
    }
  }

  function getButtonsForSave(): LandingTemplateButtonsMap {
    return serializeLandingTemplateButtonsForSave(buttons.value)
  }

  return {
    buttons,
    isButtonTypePopupOpen,
    loadButtonsFromContent,
    handleStartAddButton,
    handleOpenModuleSelect,
    handleChooseButtonType,
    handleClearButton,
    getButtonsForSave,
    clearDraft,
  }
}
